import React, { useEffect, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { Ring } from '../types';
import { Modal } from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast, ToastMessage } from '../components/Toast';
import { Gem, PlusCircle, Trash2, Edit2 } from 'lucide-react';
import { syncRingToBudget } from '../lib/budget';
import { Button } from '../components/ui/Button';

const INITIAL_RING: Partial<Ring> = {
  style_name: '',
  material: 'Zlato',
  price_estimate: 0,
  paid_amount: 0,
  status: 'potential',
  owner: 'nevesta',
  ring_type: 'snubni'
};

export default function RingsPage() {
  const { wedding, locale } = useWedding();
  const t = (key: string) => getTranslation(locale, key);

  const [rings, setRings] = useState<Ring[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Ring>>(INITIAL_RING);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchRings = async () => {
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const { data } = await supabase.from('ring_items').select('*').eq('wedding_id', wedding.id);
    if (data) setRings(data);
  };

  useEffect(() => {
    fetchRings();
  }, [wedding]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    let result;
    if (formData.id) {
      result = await supabase.from('ring_items').update(formData).eq('id', formData.id).select();
    } else {
      result = await supabase.from('ring_items').insert({ ...formData, wedding_id: wedding.id }).select();
    }
    if (result.error) { showToast('error', `Chyba: ${result.error.message}`); return; }
    if (result.data?.[0]) await syncRingToBudget(result.data[0]);
    showToast('success', t('common.save'));
    setIsModalOpen(false);
    fetchRings();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('ring_items').delete().eq('id', deleteId);
    setDeleteId(null);
    if (error) showToast('error', `Chyba: ${error.message}`);
    else fetchRings();
  };

  if (!wedding) return null;

  const renderSection = (title: string, items: Ring[], anchor: string) => (
    <div id={anchor} className="space-y-6 scroll-mt-24">
      <h2 className="text-ds-h2 text-slate-900 dark:text-white flex items-center gap-2">
        <Gem size={20} className="text-brand-500" /> {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.length === 0 ? (
           <div className="col-span-full p-8 bg-slate-50 dark:bg-slate-900/50 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl text-center text-slate-400 text-sm">
             Zatím žádné prstýnky v této sekci.
           </div>
        ) : (
          items.map(ring => (
             <div key={ring.id} className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-ds-border transition-all group">
                <div className="flex justify-between items-start mb-4">
                   <div className={`px-2.5 py-1 rounded-full text-ds-meta font-medium ${
                      ring.status === 'confirmed' ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' :
                      ring.status === 'canceled' ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400' : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                   }`}>
                      {ring.status === 'confirmed' ? t('rings.status_confirmed') : ring.status === 'canceled' ? t('rings.status_canceled') : t('rings.status_potential')}
                   </div>
                   <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => { setFormData(ring); setIsModalOpen(true); }} className="p-1.5 text-slate-400 hover:text-brand-600"><Edit2 size={16} /></button>
                      <button onClick={() => setDeleteId(ring.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                   </div>
                </div>
                <h3 className="text-ds-h3 font-semibold text-slate-900 dark:text-white mb-1">{ring.style_name}</h3>
                <p className="text-sm text-slate-500 mb-4">{ring.material} • Velikost: {ring.size || '-'}</p>
                {ring.engraving_text && (
                   <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-xl text-xs text-slate-600 dark:text-slate-400 italic mb-4 text-center border border-slate-100 dark:border-slate-800">
                      "{ring.engraving_text}"
                   </div>
                )}
                <div className="pt-4 border-t border-slate-50 dark:border-slate-800 flex justify-between items-center text-sm">
                   <span className="text-slate-400">Cena:</span>
                   <span className="text-slate-900 dark:text-white font-medium">{(ring.price_estimate || 0).toLocaleString()} Kč</span>
                </div>
             </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-12 animate-fade-in">
       <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
             <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('rings.title')}</h1>
             <p className="text-ds-body text-slate-500 dark:text-slate-400 mt-2">{t('rings.subtitle')}</p>
          </div>
          <Button 
             onClick={() => { setFormData(INITIAL_RING); setIsModalOpen(true); }}
             variant="secondary"
             icon={<PlusCircle size={18} />}
          >
             {t('common.add')}
          </Button>
       </div>

       {renderSection(t('rings.bride'), rings.filter(r => r.owner === 'nevesta'), 'nevesta')}
       {renderSection(t('rings.groom'), rings.filter(r => r.owner === 'zenich'), 'zenich')}

       <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={formData.id ? 'Upravit prsten' : 'Nový prsten'}>
          <form onSubmit={handleSave} className="space-y-6">
             <div>
                <label className="text-ds-meta text-slate-500 mb-2 block">{t('rings.style')}</label>
                <input type="text" required value={formData.style_name || ''} onChange={e => setFormData({ ...formData, style_name: e.target.value })} className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500" />
             </div>
             <div className="grid grid-cols-2 gap-4">
                <div>
                   <label className="text-ds-meta text-slate-500 mb-2 block">{t('guests.form.side')}</label>
                   <select value={formData.owner} onChange={e => setFormData({...formData, owner: e.target.value as any})} className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500">
                      <option value="nevesta">{t('guests.form.bride')}</option>
                      <option value="zenich">{t('guests.form.groom')}</option>
                   </select>
                </div>
                <div>
                   <label className="text-ds-meta text-slate-500 mb-2 block">{t('rings.material')}</label>
                   <input type="text" value={formData.material || ''} onChange={e => setFormData({ ...formData, material: e.target.value })} className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
             </div>
             <div className="grid grid-cols-2 gap-4">
                <div>
                   <label className="text-ds-meta text-slate-500 mb-2 block">{t('rings.size')}</label>
                   <input type="text" value={formData.size || ''} onChange={e => setFormData({ ...formData, size: e.target.value })} className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
                <div>
                   <label className="text-ds-meta text-slate-500 mb-2 block">{t('rings.price')}</label>
                   <input type="number" value={formData.price_estimate || 0} onChange={e => setFormData({ ...formData, price_estimate: parseFloat(e.target.value) })} className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
             </div>
             <div>
                <label className="text-ds-meta text-slate-500 mb-2 block">{t('rings.status')}</label>
                <select value={formData.status || 'potential'} onChange={e => setFormData({ ...formData, status: e.target.value as Ring['status'] })} className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500">
                   <option value="potential">{t('rings.status_potential')}</option>
                   <option value="confirmed">{t('rings.status_confirmed')}</option>
                   <option value="canceled">{t('rings.status_canceled')}</option>
                </select>
             </div>
             <div>
                <label className="text-ds-meta text-slate-500 mb-2 block">{t('rings.engraving')}</label>
                <input type="text" value={formData.engraving_text || ''} onChange={e => setFormData({ ...formData, engraving_text: e.target.value })} className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500" />
             </div>
             <button type="submit" className="w-full bg-brand-600 text-white font-bold py-4 rounded-xl hover:bg-brand-700 transition-colors">{t('common.save')}</button>
          </form>
       </Modal>
       <ConfirmDialog isOpen={!!deleteId} onCancel={() => setDeleteId(null)} onConfirm={handleDelete} title={t('common.delete')} description={t('common.confirm_delete')} />

      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}